import { type Language } from '@/features/language/types/language-types';

export const DEFAULT_LANGUAGE: Language = {
  direction: 'ltr',
  english_name: 'English (US)',
  locale: 'en-US',
  name: 'English (US)',
};

export const SUPPORTED_LOCALES: Language['locale'][] = [
  'ar-SA',
  'ca-ES',
  'cs-CZ',
  'da-DK',
  'de-DE',
  'el-GR',
  'en-AU',
  'en-CA',
  'en-IE',
  'en-NZ',
  'en-SG',
  'en-GB',
  'en-US',
  'en-ZA',
  'es-ES',
  'es-MX',
  'fil-PH',
  'fr-FR',
  'he-IL',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ru-RU',
  'sv-SE',
  'tr-TR',
  'zh-CN',
  'zh-TW',
];

// Locales whose search-results pages are ready to be indexed.
//
// A locale earns a place here once its SEO content is actually translated.
// Until then its pages are near-duplicates of en-US competing with it for the
// same queries: measured 2026-08-27, the `seo` message block is 9,999,881 bytes
// in en-US and 2,436 in de-DE (0.024%), and en-GB/en-AU/en-CA render
// byte-identical HTML to each other -- same H1, same USD prices, each claiming
// itself canonical. Search Console showed position 11.2 -> 15.3 and CTR
// 3.03% -> 2.14% over 3-15 Aug while impressions rose, which is dilution.
//
// This is an allowlist rather than a blocklist on purpose. Localisation is on
// the roadmap, and a locale that gets translated should be switched on by one
// deliberate addition here -- not by someone remembering to delete it from a
// list of exclusions.
//
// Scoped to the two markets that matter: the US and Canada are ~95% of
// revenue, so what is left indexable is what serves them.
//
// The US is already fine -- 94.6% of US clicks land on the unprefixed pages.
// Canada is not. Measured over the 28 days to 2026-09-02 (Search Console API,
// country=can, dimension=page), only 16.8% of Canadian clicks reached the
// canonical URL:
//
//   en-GB 41.6%   en-CA 22.2%   unprefixed 16.8%   en-IE 4.9%
//   en-ZA  3.7%   en-NZ  3.6%   en-AU       3.2%   en-SG 2.3%
//
// Google is choosing between eight byte-identical duplicates and a UK locale
// wins most often. For "roommates toronto" the en-GB URL sits at position 3.1
// with 29 clicks while the unprefixed canonical sits at 6.0 with 3; the brand
// query "roomster" in Canada is answered by /en-IE at position 1.0, above the
// real home page.
//
// So this blocks every English regional for a country that is not a market --
// en-GB, en-IE, en-ZA, en-NZ, en-AU, en-SG -- along with the small
// non-English locales. Together 11,056 clicks over 28 days, 14.1% of site
// traffic, but 59.3% of Canadian clicks: traffic already landing on the wrong
// URL for content identical to the right one.
//
// Deliberately still indexed, pending a later read: en-CA (a real market) and
// the two Spanish locales (3.5% of US clicks, plausibly serving US Hispanic
// search). Whether en-CA should also fold into the unprefixed set is a
// separate decision that wants 3-6 weeks of history from this change first.
export const SEARCH_RESULTS_INDEXABLE_LOCALES: Language['locale'][] = [
  'en-US',
  'en-CA',
  'es-ES',
  'es-MX',
];

// Everything else. Derived so the two lists can never drift apart.
export const SEARCH_RESULTS_NOINDEX_LOCALES: Language['locale'][] = SUPPORTED_LOCALES
  .filter((locale) => !SEARCH_RESULTS_INDEXABLE_LOCALES.includes(locale));

export const LOCALE_PREFIX = 'as-needed';
